import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, PointerEvent } from 'react';
import { Client, type Room } from '@colyseus/sdk';
import {
  ACTIVE_SKILLS, CHARACTERS, GAME, MATCH_PRESETS, PASSIVE_SKILLS, TEAM, normalizeNickname, normalizeRoomCode,
  type CharacterDefinition, type GameState, type PlayerState,
} from '@ygtopu/shared';
import type { GameSnapshot } from '../game/GameCanvas';
import { localInput } from '../game/local-input';
import { audio } from './audio';
import './character.css';
import './skill.css';

const GameCanvas = lazy(() => import('../game/GameCanvas').then(({ GameCanvas: canvas }) => ({ default: canvas })));

function getServerUrl(): string {
  const configured = import.meta.env.VITE_GAME_SERVER_URL;
  if (configured) return configured.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:');
  if (import.meta.env.DEV) return `${window.location.protocol}//${window.location.hostname}:2567`;
  return `${window.location.protocol}//${window.location.host}`;
}

function getGuestId(): string {
  const key = 'ygtopu.guestId';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

function getSavedName(): string { return localStorage.getItem('ygtopu.nickname') ?? ''; }

function readSnapshot(state: GameState | undefined | null): GameSnapshot | null {
  // Colyseus can resolve the join promise before the first schema patch creates
  // the client-side MapSchema. Wait for that initial patch instead of crashing
  // the React tree while reading players.values().
  if (!state?.players || typeof state.players.values !== 'function') return null;
  return {
    phase: state.phase, roomCode: state.roomCode, ownerId: state.ownerId,
    presetId: state.presetId, durationSeconds: state.durationSeconds, goalLimit: state.goalLimit,
    goldenGoal: state.goldenGoal, skillsEnabled: state.skillsEnabled,
    friendlyCollision: state.friendlyCollision, campingPenalty: state.campingPenalty,
    threePlayerTeam: state.threePlayerTeam, blueScore: state.blueScore, redScore: state.redScore,
    remainingMs: state.remainingMs, ballX: state.ballX, ballY: state.ballY,
    ballVx: state.ballVx, ballVy: state.ballVy, ballAngularVelocity: state.ballAngularVelocity, countdown: state.countdown,
    lastScorer: state.lastScorer, winner: state.winner,
    players: Array.from(state.players.values()).map((player) => ({
      id: player.id, nickname: player.nickname, team: player.team, characterId: player.characterId,
      ready: player.ready, connected: player.connected, x: player.x, y: player.y, headOffsetY: player.headOffsetY, vx: player.vx,
      vy: player.vy, facing: player.facing, frozenUntil: player.frozenUntil, campMs: player.campMs,
      skillReadyAt: player.skillReadyAt, skillActivatedAt: player.skillActivatedAt,
      activeUntil: player.activeUntil, activeSkillId: player.activeSkillId,
      headHitAt: player.headHitAt, shotAt: player.shotAt,
      emote: player.emote, emoteUntil: player.emoteUntil, score: player.score,
      rocketShotAt: player.rocketShotAt, hurtUntil: player.hurtUntil,
    } as PlayerState)),
  };
}

function formatTime(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60).toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`;
}

export function App() {
  const client = useMemo(() => new Client(getServerUrl()), []);
  const [room, setRoom] = useState<Room | null>(null);
  const [snapshot, setSnapshot] = useState<GameSnapshot | null>(null);
  const [nickname, setNickname] = useState(getSavedName());
  const [roomCode, setRoomCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [connection, setConnection] = useState<'connected' | 'reconnecting'>('connected');
  const [campWarning, setCampWarning] = useState(0);
  const [rematchVotes, setRematchVotes] = useState({ votes: 0, total: 0 });
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [portrait, setPortrait] = useState(window.matchMedia('(orientation: portrait)').matches);
  const noticeTimer = useRef<number | undefined>(undefined);
  const intentionalLeave = useRef(false);

  useEffect(() => {
    const listener = () => setPortrait(window.matchMedia('(orientation: portrait)').matches);
    window.addEventListener('resize', listener);
    window.addEventListener('orientationchange', listener);
    return () => { window.removeEventListener('resize', listener); window.removeEventListener('orientationchange', listener); };
  }, []);

  useEffect(() => {
    const syncFullscreen = () => setIsFullscreen(Boolean(document.fullscreenElement));
    const syncViewport = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--viewport-height', `${height}px`);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    window.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('resize', syncViewport);
    syncViewport();
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      window.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('resize', syncViewport);
      document.documentElement.style.removeProperty('--viewport-height');
    };
  }, []);

  useEffect(() => {
    if (!room) return;
    let active = true;
    room.reconnection.enabled = true;
    room.reconnection.minUptime = 0;
    room.reconnection.maxRetries = 6;
    room.reconnection.delay = 125;
    room.reconnection.maxDelay = 4000;
    const update = (state: GameState) => {
      if (!active) return;
      const nextSnapshot = readSnapshot(state);
      if (nextSnapshot) setSnapshot(nextSnapshot);
    };
    room.onStateChange(update);
    update(room.state as GameState);
    room.onMessage('error', (payload: { message?: string }) => setError(payload.message ?? 'Bir hata oluştu.'));
    room.onMessage('notice', (payload: { text?: string }) => {
      setNotice(payload.text ?? '');
      if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
      noticeTimer.current = window.setTimeout(() => setNotice(''), 3600);
    });
    room.onMessage('ownerChanged', () => setNotice('Oda sahibi değişti.'));
    room.onMessage('goal', () => audio.play('goal'));
    room.onMessage('phase', (payload: { phase: string }) => { if (payload.phase === 'COUNTDOWN') audio.play('countdown'); });
    room.onMessage('frozen', () => audio.play('skill'));
    room.onMessage('playerHit', () => audio.play('kick'));
    room.onMessage('campWarning', (payload: { seconds: number }) => setCampWarning(payload.seconds));
    room.onMessage('rematchVotes', (payload: { votes: number; total: number }) => setRematchVotes(payload));
    room.onDrop(() => {
      if (!active || intentionalLeave.current) return;
      setConnection('reconnecting');
      setNotice('Bağlantı koptu. Yeniden bağlanıyor...');
    });
    room.onReconnect(() => {
      if (!active) return;
      setConnection('connected');
      setNotice('Bağlantı yeniden kuruldu.');
    });
    room.onLeave(() => {
      if (!active || intentionalLeave.current) return;
      setRoom(null);
      setSnapshot(null);
      setConnection('connected');
      setError('Bağlantı geri gelmedi. Odaya yeniden katılmak için oda kodunu kullan.');
    });
    return () => { active = false; if (noticeTimer.current) window.clearTimeout(noticeTimer.current); };
  }, [room]);

  const lobbyCharacterKey = snapshot?.phase === 'LOBBY'
    ? [...new Set(snapshot.players.map(({ characterId }) => characterId))].sort().join(',')
    : '';

  useEffect(() => {
    if (!room || snapshot?.phase !== 'LOBBY') return;
    // Fetch the game renderer and decode only the selected character sheets
    // while players are still in the lobby, so kickoff does not wait on art.
    const characterIds = lobbyCharacterKey ? lobbyCharacterKey.split(',') : [];
    void import('../game/GameCanvas').then(({ preloadCharacterSprites }) => preloadCharacterSprites(characterIds));
  }, [room, snapshot?.phase, lobbyCharacterKey]);

  async function toggleFullscreen() {
    if (isPseudoFullscreen) {
      setIsPseudoFullscreen(false);
      return;
    }
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
      return;
    }
    const gameElement = document.querySelector<HTMLElement>('.game-shell');
    if (gameElement?.requestFullscreen) {
      try {
        await gameElement.requestFullscreen({ navigationUI: 'hide' });
        return;
      } catch {
        // Mobile browsers may reject the Fullscreen API. Use the viewport instead.
      }
    }
    setIsPseudoFullscreen(true);
  }

  async function connect(kind: 'create' | 'join') {
    const safeName = normalizeNickname(nickname);
    if (!safeName) { setError('Lütfen önce ismini yaz.'); return; }
    if (kind === 'join' && normalizeRoomCode(roomCode).length !== GAME.roomCodeLength) { setError('Oda kodu 6 karakter olmalı.'); return; }
    audio.unlock();
    setError('');
    setConnection('connected');
    localStorage.setItem('ygtopu.nickname', safeName);
    setNickname(safeName);
    const options = { nickname: safeName, guestId: getGuestId() };
    try {
      const joined = kind === 'create'
        ? await client.create('match', options)
        : await client.joinById(normalizeRoomCode(roomCode), options);
      setRoom(joined);
      const initialSnapshot = readSnapshot(joined.state as GameState | undefined);
      if (initialSnapshot) setSnapshot(initialSnapshot);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : '';
      setError(message.includes('not found') || message.includes('does not exist') ? 'Bu oda koduyla bir oda bulamadım.' : `Sunucuya bağlanılamadı. ${message ? 'Bağlantını kontrol edip tekrar dene.' : 'Birazdan tekrar dene.'}`);
    }
  }

  async function leave() {
    intentionalLeave.current = true;
    try {
      if (document.fullscreenElement) await document.exitFullscreen().catch(() => undefined);
      if (room) await room.leave();
    } finally {
      setIsPseudoFullscreen(false);
      setRoom(null); setSnapshot(null); setError(''); setNotice(''); setConnection('connected');
      intentionalLeave.current = false;
    }
  }

  if (room && !snapshot) {
    return <main className="app-shell" style={{ display: 'grid', placeItems: 'center' }}>
      <div className="canvas-loading" role="status">Oda hazırlanıyor...</div>
    </main>;
  }

  if (room && snapshot) {
    const me = snapshot.players.find((player) => player.id === room.sessionId);
    const myCharacter = CHARACTERS.find(({ id }) => id === me?.characterId);
    const players = snapshot.players;
    const bluePlayers = players.filter((player) => player.team === 'blue');
    const redPlayers = players.filter((player) => player.team === 'red');
    const isOwner = snapshot.ownerId === room.sessionId;
    const match = snapshot;
    const fullscreenActive = isFullscreen || isPseudoFullscreen;
    return <main className={`app-shell game-shell${snapshot.phase === 'LOBBY' ? '' : ' is-playing'}${isPseudoFullscreen ? ' is-pseudo-fullscreen' : ''}`}>
      <header className="game-topbar">
        <a className="brand brand-small" href="#" onClick={(event) => event.preventDefault()}><span className="brand-ball">Y</span><span>YG<span>Topu</span></span></a>
        <div className="room-pill"><span className="live-dot" /> ODA <strong>{snapshot.roomCode}</strong></div>
        <div className="top-actions">
          <button className="icon-button sound-button" onClick={() => { setMuted(!muted); audio.setMuted(!muted); }} aria-label={muted ? 'Sesi aç' : 'Sesi kapat'}>{muted ? '🔇' : '🔊'}</button>
          <button className="icon-button fullscreen-button" onClick={() => void toggleFullscreen()} aria-label={fullscreenActive ? 'Tam ekrandan çık' : 'Tam ekran'} aria-pressed={fullscreenActive}>{fullscreenActive ? '×' : '⛶'}</button>
          <span className={`connection-pill ${connection}`}>{connection === 'connected' ? '● Bağlı' : '↻ Bağlantı bekleniyor'}</span>
          <button className="text-button" onClick={() => void leave()}>Çık</button>
        </div>
      </header>

      {notice && <div className="toast" role="status">{notice}</div>}
      {error && <div className="toast error-toast" role="alert"><span>{error}</span><button onClick={() => setError('')}>×</button></div>}
      {portrait && <div className="rotate-hint">↻ Oyunu daha rahat oynamak için cihazını yatay çevir</div>}

      {snapshot.phase === 'LOBBY' ? <section className="lobby-layout">
        <div className="lobby-heading"><div><p className="eyebrow">ARKADAŞLARINLA OYNA</p><h1>Maç lobisi</h1><p className="subheading">Takımını seç, karakterini hazırla ve oyuna gir.</p></div><button className="share-code" onClick={() => void navigator.clipboard?.writeText(snapshot.roomCode).then(() => setNotice('Oda kodu kopyalandı!'))}><span>ODA KODU</span><strong>{snapshot.roomCode}</strong><small>Paylaş ↗</small></button></div>
        <div className="lobby-main">
          <div className="teams-grid">
            <TeamColumn team="blue" players={bluePlayers} targetCount={redPlayers.filter((player) => player.connected).length} me={me} room={room} />
            <div className="versus">VS</div>
            <TeamColumn team="red" players={redPlayers} targetCount={bluePlayers.filter((player) => player.connected).length} me={me} room={room} />
          </div>
          {me && <CharacterPicker selectedId={me.characterId} players={players} room={room} />}
          <div className="lobby-bottom">
            <div className="my-ready"><div className="ready-status"><span className={me?.ready ? 'ready-dot is-ready' : 'ready-dot'} />{me?.ready ? 'Hazırsın!' : 'Hazır olunca bildir'}</div><button className={`primary-button ready-button ${me?.ready ? 'is-ready' : ''}`} onClick={() => room.send('ready', !me?.ready)}>{me?.ready ? '✓ HAZIRIM' : 'HAZIRIM'}</button></div>
            {isOwner ? <button className="primary-button start-button" onClick={() => { setError(''); room.send('start'); }}>MAÇI BAŞLAT <span>→</span></button> : <div className="waiting-owner"><span className="pulse-dot" /> Oda sahibi maçı başlatacak…</div>}
          </div>
          {isOwner && <ConfigPanel snapshot={snapshot} room={room} />}
          <div className="how-to-play"><span>⌨️ A / D</span> hareket <span>SPACE / ↑</span> zıpla <span>J</span> şut (basılı tut: sert şut) <span>K</span> skill <span>1–4</span> emote <small>Mobilde ekran kontrollerini kullan</small></div>
        </div>
      </section> : <section className="match-layout" onContextMenu={(event) => event.preventDefault()}>
        <div className="scorebar">
          <div className="score-team blue-score"><span className="team-dot" /> MAVİ <b>{snapshot.blueScore}</b><small>{bluePlayers.length} oyuncu</small></div>
          <div className="score-clock">{snapshot.phase === 'OVERTIME' ? <><span className="overtime">GOLDEN GOAL</span><b>∞</b></> : <><b>{formatTime(snapshot.remainingMs)}</b><small>{snapshot.phase === 'COUNTDOWN' ? `Başlıyor ${snapshot.countdown}` : snapshot.phase === 'GOAL_PAUSE' ? 'GOL!' : 'KALAN SÜRE'}</small></>}</div>
          <div className="score-team red-score"><small>{redPlayers.length} oyuncu</small><b>{snapshot.redScore}</b> KIRMIZI <span className="team-dot" /></div>
        </div>
        <div className="arena-frame">
          <Suspense fallback={<div className="canvas-loading">Saha hazırlanıyor...</div>}>
            <GameCanvas snapshot={snapshot} myId={room.sessionId} />
          </Suspense>
          {connection === 'reconnecting' && <div className="reconnect-overlay" role="status"><span className="pulse-dot" /> Bağlantı yeniden kuruluyor...</div>}
          <div className="arena-label left-label">MAHALLE SAHASI</div><div className="arena-label right-label">{match.phase === 'PLAYING' ? '● CANLI' : match.phase === 'COUNTDOWN' ? `BAŞLIYOR · ${match.countdown}` : match.phase === 'GOAL_PAUSE' ? 'GOOOL!' : match.phase === 'OVERTIME' ? 'GOLDEN GOAL' : 'MAÇ BİTTİ'}</div>
          {snapshot.phase === 'COUNTDOWN' && <div className="center-callout"><span>HAZIR OL!</span><strong>{snapshot.countdown || 'BAŞLA!'}</strong></div>}
          {snapshot.phase === 'GOAL_PAUSE' && <div className="goal-callout"><span>⚽</span><strong>GOOOL!</strong>{snapshot.lastScorer && <small>{snapshot.lastScorer}</small>}</div>}
          {snapshot.phase === 'FINISHED' && <div className="result-card"><span className="result-emoji">{snapshot.winner === 'draw' ? '🤝' : snapshot.winner === me?.team ? '🏆' : '⚽'}</span><p>{snapshot.winner === 'draw' ? 'BERABERE!' : snapshot.winner === me?.team ? 'KAZANDIN!' : 'MAÇ BİTTİ'}</p><div className="result-score">{snapshot.blueScore}<i>—</i>{snapshot.redScore}</div><small>{snapshot.winner === 'draw' ? 'İyi maçtı!' : `${snapshot.winner === 'blue' ? 'Mavi' : 'Kırmızı'} takım kazandı`}</small><button className="primary-button rematch-button" onClick={() => room.send('rematch')}>TEKRAR OYNA {rematchVotes.votes > 0 && <em>{rematchVotes.votes}/{rematchVotes.total}</em>}</button><button className="subtle-button" onClick={() => void leave()}>Lobiye dön</button></div>}
          {campWarning > 0 && <div className="camp-warning">⚠ KALEDEN ÇIK! <b>{campWarning}</b></div>}
          {me && me.frozenUntil > Date.now() && <div className="freeze-banner">🥶 DONDUN!</div>}
        </div>
        <div className="match-footer"><div className="local-player"><span className="mini-avatar" style={{ background: `#${(myCharacter?.color ?? 0x69d7e7).toString(16)}` }}><CharacterAvatar character={myCharacter} /></span><span><strong>{me?.nickname ?? nickname}</strong><small>{myCharacter?.displayName ?? 'Oyuncu'}</small></span><span className="skill-chip" title={myCharacter ? ACTIVE_SKILLS[myCharacter.activeSkillId]?.description : undefined}>{snapshot.skillsEnabled && myCharacter ? ACTIVE_SKILLS[myCharacter.activeSkillId]?.name : 'Skill kapalı'} <i>{!snapshot.skillsEnabled ? '' : me && me.skillReadyAt > Date.now() ? `${Math.ceil((me.skillReadyAt - Date.now()) / 1000)}s` : 'K: HAZIR'}</i></span></div><div className="match-hint">{myCharacter && snapshot.skillsEnabled ? ACTIVE_SKILLS[myCharacter.activeSkillId]?.description : 'Topa yaklaş, kafanla vur ya da J ile şut çek; J\'yi basılı tut, sert vur'}</div><div className="ping">● <span>BAĞLI</span></div></div>
        {snapshot.phase !== 'FINISHED' && <GameControls room={room} character={myCharacter} skillReadyAt={me?.skillReadyAt ?? 0} skillsEnabled={snapshot.skillsEnabled} />}
      </section>}
      <footer className="site-footer">YGTopu <span>·</span> Mahalle maçı başlasın</footer>
    </main>;
  }

  return <main className="app-shell home-shell">
    <div className="ambient ambient-one" /><div className="ambient ambient-two" />
    <header className="home-topbar"><a className="brand" href="#" onClick={(event) => event.preventDefault()}><span className="brand-ball">Y</span><span>YG<span>Topu</span></span></a><div className="online-badge"><span className="live-dot" /> ARKADAŞLARINLA OYNA</div><button className="help-button" title="Kontroller">? <span>Nasıl oynanır</span></button></header>
    <section className="hero">
      <div className="hero-copy"><div className="hero-kicker"><span>⚽</span> MAHALLE MAÇI BAŞLASIN</div><h1>Kafa kafaya.<br/><em>Gülmek serbest.</em></h1><p>Arkadaşlarını çağır, takımını kur. Kafalar büyük, rekabet tatlı!</p>
        <div className="feature-pills"><span>👥 1–5 arkadaş</span><span>📱 Mobil + bilgisayar</span><span>🎮 Ücretsiz oda</span></div>
      </div>
      <div className="hero-scene home-roster" aria-label="Kemal, Burak, Berken, Can ve Umut karakterleri">
        <div className="home-roster-heading"><span>SAHAYA ÇIKACAK EKİP</span><span>⚽</span></div>
        <div className="home-character-strip">
          {CHARACTERS.slice(0, 5).map((character) => <div className="home-character-card" key={character.id}>
            <div className="home-character-art" data-character-id={character.id} style={{ borderColor: `#${character.color.toString(16)}` }}>
              {character.spriteSheet ? <span className="home-character-sheet" style={{ backgroundImage: `url(${character.spriteSheet})` }} /> : <CharacterAvatar character={character} />}
            </div>
            <strong>{character.displayName}</strong>
            <small>{ACTIVE_SKILLS[character.activeSkillId]?.name}</small>
          </div>)}
        </div>
      </div>
    </section>

    <section className="play-card">
      <div className="play-card-top"><div><span className="step-number">01</span><label>ÖNCE BİR İSİM SEÇ</label></div><small>Hesap yok, direkt oyun</small></div>
      <label className="input-label" htmlFor="nickname">Oyuncu adı</label>
      <div className="name-input-wrap"><span className="input-emoji">🙂</span><input id="nickname" value={nickname} maxLength={GAME.nicknameMaxLength} placeholder="Örn. Kaptan" onChange={(event) => setNickname(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void connect('create'); }} /><span className="char-count">{normalizeNickname(nickname).length}/{GAME.nicknameMaxLength}</span></div>
      <button className="primary-button create-button" onClick={() => void connect('create')}><span>ODA OLUŞTUR</span><span className="button-arrow">→</span></button>
      <div className="or-divider"><span/> YA DA ODA KODUN VARSA <span/></div>
      <div className="join-row"><div className="code-input-wrap"><span>⌑</span><input value={roomCode} maxLength={GAME.roomCodeLength} placeholder="6 HANELİ KOD" aria-label="Oda kodu" onChange={(event) => setRoomCode(normalizeRoomCode(event.target.value))} onKeyDown={(event) => { if (event.key === 'Enter') void connect('join'); }} /></div><button className="join-button" onClick={() => void connect('join')}>KATIL <span>→</span></button></div>
      {error && <div className="home-error" role="alert">⚠ {error}<button onClick={() => setError('')}>×</button></div>}
      <div className="privacy-note"><span>🔒</span> Özel oda. Sadece kodunu paylaştığın arkadaşların katılır.</div>
    </section>

    <div className="home-bottom"><div className="mode-chips"><span>1 v 1</span><span>2 v 2</span><span className="chaos-chip">2 v 3 kaos!</span></div><div className="credits">Birlikte oynamak daha güzel <span>♥</span></div></div>
    <div className="home-footer">YGTopu <span>·</span> Küçük bir mahalle klasiği</div>
    <button className="mobile-help" onClick={() => setNotice('A/D hareket · SPACE zıpla · J şut · K skill · Maçta 1–4 emote')}>Kontroller <span>ⓘ</span></button>
    {notice && <div className="toast">{notice}</div>}
  </main>;
}

function CharacterAvatar({ character }: { character: CharacterDefinition | undefined }) {
  if (!character) return '??';
  if (character.spriteSheet) return <span className="sheet-avatar" style={{ backgroundImage: `url(${character.spriteSheet})` }} aria-hidden="true" />;
  if (character.image) return <img src={character.image} alt="" />;
  return character.headEmoji;
}

const RADAR_AXES = [
  { key: 'speed', label: 'HIZ', name: 'Hız' },
  { key: 'acceleration', label: 'İVME', name: 'İvme' },
  { key: 'jump', label: 'ZIPLAMA', name: 'Zıplama' },
  { key: 'shotPower', label: 'ŞUT', name: 'Şut' },
  { key: 'strength', label: 'GÜÇ', name: 'Güç' },
  { key: 'balance', label: 'DENGE', name: 'Denge' },
  { key: 'weight', label: 'AĞIRLIK', name: 'Ağırlık' },
] as const satisfies ReadonlyArray<{ key: keyof CharacterDefinition['stats']; label: string; name: string }>;

function CharacterPicker({ selectedId, players, room }: { selectedId: string; players: PlayerState[]; room: Room }) {
  const [profilePlayerId, setProfilePlayerId] = useState(room.sessionId);
  const profilePlayer = players.find((player) => player.id === profilePlayerId)
    ?? players.find((player) => player.id === room.sessionId)
    ?? players[0];
  const profileCharacter = CHARACTERS.find((character) => character.id === profilePlayer?.characterId) ?? CHARACTERS[0]!;
  const selectedSkill = ACTIVE_SKILLS[profileCharacter.activeSkillId];
  const passiveSkill = PASSIVE_SKILLS[profileCharacter.passiveSkillId];
  const accent = `#${profileCharacter.color.toString(16).padStart(6, '0')}`;
  return <div className="character-picker">
    <div className="character-picker-heading"><span>KARAKTERİNİ SEÇ</span><small>Seçim hazır durumunu sıfırlar</small></div>
    <div className="character-options" role="group" aria-label="Karakter seçimi">
      {CHARACTERS.map((character) => <button key={character.id} type="button" className={`character-option ${selectedId === character.id ? 'selected' : ''}`} aria-label={character.displayName} aria-pressed={selectedId === character.id} onClick={() => { setProfilePlayerId(room.sessionId); room.send('character', character.id); }}>
        <span className="character-option-avatar" style={{ backgroundColor: `#${character.color.toString(16)}` }}><CharacterAvatar character={character} /></span>
        <strong>{character.displayName}</strong>
        <small title={ACTIVE_SKILLS[character.activeSkillId]?.description}>{ACTIVE_SKILLS[character.activeSkillId]?.name}</small>
      </button>)}
    </div>
    <section className="character-profile" aria-label="Oyuncu yetenekleri">
      <div className="character-profile-heading">
        <strong>YETENEK RADARI</strong>
        <span>Oyuncu seç</span>
      </div>
      <div className="character-profile-tabs" role="group" aria-label="Yetenek grafiğini gösterilecek oyuncu">
        {players.map((player) => <button key={player.id} type="button" aria-label={`${player.nickname} yetenek grafiğini göster`} aria-pressed={profilePlayer?.id === player.id} className={profilePlayer?.id === player.id ? 'active' : ''} onClick={() => setProfilePlayerId(player.id)}>
          {player.nickname}{player.id === room.sessionId && <small>SEN</small>}
        </button>)}
      </div>
      <div className="character-profile-content">
        <div className="radar-wrap">
          <div className="radar-player-name"><span style={{ backgroundColor: accent }} />{profilePlayer?.nickname} · {profileCharacter.displayName}</div>
          <CharacterRadar character={profileCharacter} playerName={profilePlayer?.nickname ?? 'Oyuncu'} accent={accent} />
        </div>
        <div className="character-profile-details">
          <div className="character-stat-grid">
            {RADAR_AXES.map(({ key, name }) => <div className="character-stat-item" key={key}>
              <span>{name}</span><strong>{profileCharacter.stats[key]}</strong>
              <i><b style={{ width: `${profileCharacter.stats[key]}%`, backgroundColor: accent }} /></i>
            </div>)}
          </div>
          <div className="character-profile-skills">
            {passiveSkill && <div><small>PASİF</small><strong>{passiveSkill.name}</strong><span>{passiveSkill.description}</span></div>}
            {selectedSkill && <div><small>AKTİF · K</small><strong>{selectedSkill.name}</strong><span>{selectedSkill.description}</span></div>}
          </div>
          <p className="radar-control-note">Skill: masaüstünde K · mobilde güç tuşu</p>
        </div>
      </div>
    </section>
  </div>;
}

function CharacterRadar({ character, playerName, accent }: { character: CharacterDefinition; playerName: string; accent: string }) {
  const center = { x: 120, y: 112 };
  const radius = 76;
  const point = (distance: number, index: number) => {
    const angle = (index / RADAR_AXES.length) * Math.PI * 2 - Math.PI / 2;
    return { x: center.x + Math.cos(angle) * distance, y: center.y + Math.sin(angle) * distance };
  };
  const coordinates = (distance: number) => RADAR_AXES.map((_, index) => {
    const { x, y } = point(distance, index);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const values = RADAR_AXES.map(({ key }) => character.stats[key]);
  const valueCoordinates = values.map((value, index) => {
    const { x, y } = point(radius * value / 100, index);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return <svg className="character-radar-svg" viewBox="0 0 240 224" role="img" aria-label={`${playerName} · ${character.displayName} yetenek grafiği: ${RADAR_AXES.map(({ name }, index) => `${name} ${values[index]}`).join(', ')}`}>
    <title>{playerName} · {character.displayName} karakter yetenekleri</title>
    {[25, 50, 75, 100].map((level) => <polygon key={level} points={coordinates(radius * level / 100)} className="radar-grid" />)}
    {RADAR_AXES.map(({ label }, index) => {
      const edge = point(radius, index);
      const labelPoint = point(radius + 23, index);
      const anchor = labelPoint.x < center.x - 12 ? 'end' : labelPoint.x > center.x + 12 ? 'start' : 'middle';
      const baseline = labelPoint.y < center.y - 15 ? 'auto' : labelPoint.y > center.y + 15 ? 'hanging' : 'middle';
      return <g key={label}>
        <line x1={center.x} y1={center.y} x2={edge.x} y2={edge.y} className="radar-axis" />
        <text x={labelPoint.x} y={labelPoint.y} textAnchor={anchor} dominantBaseline={baseline} className="radar-axis-label">{label}</text>
      </g>;
    })}
    <polygon points={valueCoordinates} fill={accent} fillOpacity=".34" stroke={accent} strokeWidth="2.5" strokeLinejoin="round" />
    {values.map((value, index) => {
      const position = point(radius * value / 100, index);
      return <circle key={RADAR_AXES[index]!.key} cx={position.x} cy={position.y} r="3.5" fill={accent} stroke="#f1fbff" strokeWidth="1.5" />;
    })}
  </svg>;
}

function TeamColumn({ team, players, targetCount, me, room }: { team: 'blue' | 'red'; players: PlayerState[]; targetCount: number; me: PlayerState | undefined; room: Room }) {
  const color = TEAM[team];
  return <div className={`team-panel ${team}-panel`}>
    <div className="team-panel-head"><div className="team-name"><span className="team-dot" />{color.label.toUpperCase()} TAKIM</div><span className="team-count">{players.length}<i>/3</i></span></div>
    <div className="roster">{players.length === 0 && <div className="empty-slot">İlk oyuncu sen olabilirsin!</div>}{players.map((player) => {
      const character = CHARACTERS.find(({ id }) => id === player.characterId) ?? CHARACTERS[0]!;
      return <div className={`player-row ${player.id === me?.id ? 'is-me' : ''}`} key={player.id}><div className="roster-avatar" style={{ backgroundColor: `#${character.color.toString(16)}` }}><CharacterAvatar character={character} /></div><div className="player-info"><strong>{player.nickname}{player.id === room.sessionId && <small className="you-tag">SEN</small>}{player.id === room.state.ownerId && <small className="owner-tag">SAHİP</small>}</strong><span>{character.displayName} · {character.tagline}</span></div><span className={`ready-label ${player.ready ? 'ready' : ''}`}>{player.ready ? 'HAZIR' : player.connected ? 'SEÇİYOR' : 'BAĞLANIYOR'}</span></div>;
    })}</div>
    {me && <button className="team-switch" disabled={targetCount >= 3} onClick={() => room.send('team', team === 'blue' ? 'red' : 'blue')}>↔ {team === 'blue' ? 'KIRMIZI' : 'MAVİ'} TAKIMA GEÇ</button>}
  </div>;
}

function ConfigPanel({ snapshot, room }: { snapshot: GameSnapshot; room: Room }) {
  function send(key: string, value: string | boolean | number) { room.send('config', { [key]: value }); }
  const selectedPreset = MATCH_PRESETS.find(({ id }) => id === snapshot.presetId)!;
  const team3 = snapshot.threePlayerTeam as 'blue' | 'red';
  return <details className="config-panel"><summary><span>⚙ MAÇ AYARLARI</span><small>Oda sahibi düzenleyebilir</small><span className="settings-chevron">⌄</span></summary><div className="config-grid">
    <label>Oyun modu<select value={snapshot.presetId} onChange={(event) => send('presetId', event.target.value)}>{MATCH_PRESETS.map((preset) => <option value={preset.id} key={preset.id}>{preset.label}</option>)}</select></label>
    <label>Süre<select value={snapshot.durationSeconds} onChange={(event) => send('durationSeconds', Number(event.target.value))}>{[60,90,120,180].map((seconds) => <option key={seconds} value={seconds}>{seconds} saniye</option>)}</select></label>
    <label>Gol limiti<select value={snapshot.goalLimit} onChange={(event) => send('goalLimit', Number(event.target.value))}>{[0,3,5,10].map((limit) => <option key={limit} value={limit}>{limit || 'Kapalı'}</option>)}</select></label>
    {selectedPreset.bluePlayers !== selectedPreset.redPlayers && <label>{Math.max(selectedPreset.bluePlayers, selectedPreset.redPlayers)} kişilik takım<select value={team3} onChange={(event) => send('threePlayerTeam', event.target.value)}><option value="red">Kırmızı</option><option value="blue">Mavi</option></select></label>}
    <Toggle label="Golden Goal" value={snapshot.goldenGoal} onChange={(value) => send('goldenGoal', value)} />
    <Toggle label="Skill'ler" value={snapshot.skillsEnabled} onChange={(value) => send('skillsEnabled', value)} />
    <Toggle label="Takım arkadaşı çarpışması" value={snapshot.friendlyCollision} onChange={(value) => send('friendlyCollision', value)} />
    <Toggle label="Kalede bekleme cezası" value={snapshot.campingPenalty} onChange={(value) => send('campingPenalty', value)} />
  </div><small className="config-note">Takımların oyuncu sayısı seçilen moda uymalı. Ayar değişikliği herkesin hazır durumunu sıfırlar.</small></details>;
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (value: boolean) => void }) {
  return <button type="button" className={`toggle-row ${value ? 'checked' : ''}`} onClick={() => onChange(!value)}><span>{label}</span><i><b/></i></button>;
}

const MIN_JUMP_PRESS_MS = 120;

type HeldAction = 'left' | 'right' | 'jump' | 'shoot';

function GameControls({ room, character, skillReadyAt, skillsEnabled }: { room: Room; character: CharacterDefinition | undefined; skillReadyAt: number; skillsEnabled: boolean }) {
  const [emotesOpen, setEmotesOpen] = useState(false);
  const keys = useRef(new Set<HeldAction>());
  const heldPointers = useRef(new Map<number, HeldAction>());
  const edges = useRef({ jump: false, skill: false });
  const jumpPulseUntil = useRef(0);
  const sendInput = useRef<() => void>(() => undefined);

  useEffect(() => {
    const keyMap: Record<string, HeldAction> = { a: 'left', arrowleft: 'left', d: 'right', arrowright: 'right', ' ': 'jump', arrowup: 'jump', j: 'shoot' };
    const isHeld = (action: HeldAction) => keys.current.has(action) || [...heldPointers.current.values()].includes(action) || (action === 'jump' && performance.now() < jumpPulseUntil.current);
    // Sent at inputHz and immediately on every change, so presses do not wait
    // for the next timer tick. Held flags drive jump height and shot charge.
    const send = (consumeEdges = false) => {
      const left = isHeld('left');
      const right = isHeld('right');
      const moveX = left === right ? 0 : left ? -1 : 1;
      const shootHeld = isHeld('shoot');
      localInput.moveX = moveX;
      if (shootHeld && !localInput.shootHeldSince) localInput.shootHeldSince = performance.now();
      else if (!shootHeld) localInput.shootHeldSince = 0;
      room.send('input', { moveX, jump: edges.current.jump, shoot: false, skill: edges.current.skill, jumpHeld: isHeld('jump'), shootHeld });
      // Keep press edges alive until an input-tick send. A quick tap can send
      // down and up before the server's next simulation step; clearing here
      // would let the release overwrite the jump/skill press before it is read.
      if (consumeEdges) edges.current = { jump: false, skill: false };
    };
    sendInput.current = send;
    const down = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if ([' ', 'arrowup', 'arrowleft', 'arrowright'].includes(key)) event.preventDefault();
      if (event.repeat) return;
      const action = keyMap[key];
      if (action) {
        keys.current.add(action);
        if (action === 'jump') {
          edges.current.jump = true;
          jumpPulseUntil.current = performance.now() + MIN_JUMP_PRESS_MS;
        }
        send();
      } else if (key === 'k') {
        edges.current.skill = true;
        send();
      } else if (['1','2','3','4'].includes(key)) room.send('emote', ['😂','😎','😡','😭'][Number(key)-1]);
    };
    const up = (event: KeyboardEvent) => {
      const action = keyMap[event.key.toLowerCase()];
      if (action && keys.current.delete(action)) send();
    };
    const releaseAll = () => {
      keys.current.clear();
      heldPointers.current.clear();
      edges.current = { jump: false, skill: false };
      jumpPulseUntil.current = 0;
      send();
    };
    const onVisibilityChange = () => {
      if (document.hidden) releaseAll();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', releaseAll);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const timer = window.setInterval(() => send(true), 1000 / GAME.inputHz);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', releaseAll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      releaseAll();
      sendInput.current = () => undefined;
      localInput.moveX = 0;
      localInput.shootHeldSince = 0;
    };
  }, [room]);

  const releasePointer = (pointerId: number) => {
    if (heldPointers.current.delete(pointerId)) sendInput.current();
  };
  const holdProps = (action: HeldAction | 'skill') => ({
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId);
      if (action === 'skill') edges.current.skill = true;
      else heldPointers.current.set(event.pointerId, action);
      if (action === 'jump') {
        edges.current.jump = true;
        jumpPulseUntil.current = performance.now() + MIN_JUMP_PRESS_MS;
      }
      sendInput.current();
    },
    onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault(); releasePointer(event.pointerId);
    },
    onPointerCancel: (event: React.PointerEvent<HTMLButtonElement>) => { releasePointer(event.pointerId); },
    onLostPointerCapture: (event: React.PointerEvent<HTMLButtonElement>) => { releasePointer(event.pointerId); },
    onContextMenu: (event: MouseEvent) => event.preventDefault(),
  });

  return <div className="touch-controls" role="group" aria-label="Oyun kontrolleri">
    <div className="move-controls"><button type="button" aria-label="Sola hareket et" {...holdProps('left')}>◀</button><button type="button" aria-label="Sağa hareket et" {...holdProps('right')}>▶</button></div>
    <div className="action-controls"><div className="emote-picker-wrap"><button type="button" className="emote-control" aria-label="Emote menüsünü aç" aria-expanded={emotesOpen} onClick={() => setEmotesOpen((open) => !open)}>😊</button>{emotesOpen && <div className="emote-picker" role="group" aria-label="Emote seç">{['😂','😎','😡','😭'].map((emote) => <button key={emote} type="button" aria-label={`${emote} emote gönder`} onClick={() => { room.send('emote', emote); setEmotesOpen(false); }}>{emote}</button>)}</div>}</div><button type="button" className="jump-control" aria-label="Zıpla" {...holdProps('jump')}>ZIPLA</button><button type="button" className="shoot-control" aria-label="Şut çek" {...holdProps('shoot')}>ŞUT</button><button type="button" className="skill-control" aria-label="Skill kullan" title={character ? `${ACTIVE_SKILLS[character.activeSkillId]?.name}: ${ACTIVE_SKILLS[character.activeSkillId]?.description}` : undefined} disabled={!skillsEnabled || skillReadyAt > Date.now()} {...holdProps('skill')}>{character ? ACTIVE_SKILLS[character.activeSkillId]?.name : 'SKILL'}</button></div>
  </div>;
}
